import { Box, Button, Chip, Stack, Table, TableBody, TableHead, TextField, Tooltip, Typography } from '@mui/material';
import CopyIcon from '@mui/icons-material/ContentCopy';
import { StyledTableCell, StyledTableRow } from '../StyledTable';
import React, { useState } from 'react';
import { humanize } from '../util/StringUtil';
import { displayDate, displayTimeWithBrisbaneTz } from '../util/TimeUtil';
import { groupBy } from '../util/ObjectUtil';
import Divider from '@mui/material/Divider';
import { SubText } from './StyledComponent';
import MUIDataTable, { MUIDataTableOptions } from 'mui-datatables';
import dayjs from 'dayjs';
import { getFullName } from '../util/DataUtil';

/** Users who never logged in have no lastLogin; show that instead of "Invalid Date". */
export function formatLastLogin(lastLogin?: string | null) {
  return lastLogin ? displayDate(lastLogin) : 'Never';
}

export default function ManageUsersTable(props: { users: User[] }) {
  const { users } = props;

  const firms: Firm[] = [
    ...users
      .filter(f => f.firm != null)
      .reduce((a, c) => {
        if (c.firm) {
          a.set(c.firm.id || -1, c.firm);
        }
        return a;
      }, new Map<number, Firm>())
      .values(),
  ].sort((a: Firm, b: Firm) => b.creationDate.localeCompare(a.creationDate));

  const usersPerFirm = groupBy(users, user => user.firm?.id || -1);

  const columns = [
    {
      name: 'name',
      label: 'Firm',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          return (
            <div>
              {firm.name} <i>(id: {firm.id})</i>
            </div>
          );
        },
      },
    },
    {
      name: 'firm.billingUser',
      label: 'Billing contact',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          return (
            <div>
              <i>{firm.billingUser.email}</i>
              <SubText>
                {firm.billingUser.givenName} {firm.billingUser.familyName}
              </SubText>
            </div>
          );
        },
      },
    },
    {
      name: 'subscription.level',
      label: 'Subscription Level',
      options: {
        filter: true,
        sort: true,
        customBodyRender(value: string) {
          return humanize(value);
        },
      },
    },
    {
      name: 'subscription.status',
      label: 'Subscription Status',
      options: {
        filter: true,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          const trialDate = firm.subscription.status === 'TRIAL' ? ` (until ${displayDate(firm.subscription.trialEndDate)})` : '';
          return humanize(firm.subscription.status) + trialDate;
        },
      },
    },
    {
      name: 'openAccess',
      label: 'Open Access',
      options: {
        filter: true,
        sort: true,
        customBodyRender(value: boolean) {
          return value ? 'Enabled' : 'Disabled';
        },
      },
    },
    {
      name: 'connectionCount',
      label: 'Connections count (firm)',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          return (
            <div>
              {firm.connectionCount} / {firm.subscription.connectionLimit}
            </div>
          );
        },
      },
    },
    {
      name: 'firm.connectionCount',
      label: '# Users',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          return (
            <Tooltip
              title={
                <div>
                  {usersPerFirm[firm.id].map((user: User) => {
                    return (
                      <div>
                        {user.givenName} {user.familyName} ({user.id}) (Last login: {formatLastLogin(user.lastLogin)})
                      </div>
                    );
                  })}
                </div>
              }
            >
              <div>{usersPerFirm[firm.id].length}</div>
            </Tooltip>
          );
        },
      },
    },
    {
      name: 'lastLogin',
      label: 'Last login',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          const sortedUsers = [...usersPerFirm[firm.id]].sort((a, b) => {
            if (b.lastLogin == null || a.lastLogin > b.lastLogin) return -1;
            if (a.lastLogin == null || a.lastLogin < b.lastLogin) return 1;
            return 0;
          });
          return (
            <div>
              {formatLastLogin(sortedUsers[0].lastLogin)}
              <SubText>By {getFullName(sortedUsers[0])}</SubText>
            </div>
          );
        },
      },
    },
    {
      name: 'creationDate',
      label: 'Creation time (GMT+10)',
      options: {
        filter: false,
        sort: true,
        customBodyRender(value: string) {
          return displayTimeWithBrisbaneTz(value);
        },
      },
    },
    {
      name: 'updateDate',
      label: 'Last updated',
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex: number) => {
          const firm = firms[dataIndex];
          return displayDate(dayjs.max(dayjs.utc(firm.updateDate), dayjs.utc(firm.subscription.updateDate)));
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    setRowProps: (row, dataIndex) => {
      const firm = firms[dataIndex];
      if (firm.subscription.status === 'ACTIVE') {
        return {
          style: { backgroundColor: '#e7f6ec' },
        };
      }
      return {};
    },
    enableNestedDataAccess: '.',
    rowsPerPageOptions: [25, 50, 100],
    print: false,
    download: false,
    viewColumns: false,
    storageKey: 'manageUserConfig',
    selectableRowsHideCheckboxes: true,
    fixedHeader: true,
    setTableProps: () => {
      return {
        // material ui v5 only
        size: 'small',
      };
    },
  };

  const countPerSubscription = (subscriptionLevel: string) => {
    return firms.filter(firm => firm.subscription.level.toLowerCase() == subscriptionLevel.toLowerCase()).length;
  };

  const countTrials = () => {
    return firms.filter(firm => firm.subscription.status == 'TRIAL').length;
  };

  const totals = [
    { label: 'Starter', value: `${countPerSubscription('starter')} (${countTrials()} trials)` },
    { label: 'Intermediate', value: countPerSubscription('intermediate') },
    { label: 'Business', value: countPerSubscription('business') },
    { label: 'Business plus', value: countPerSubscription('business_plus') },
    { label: 'Connections', value: firms.map(firm => firm.connectionCount).reduce((a, b) => a + b, 0) },
    { label: 'Users', value: users.length },
  ];

  const title = (
    <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ py: 1 }}>
      <Typography sx={{ fontWeight: 600, mr: 1 }}>{firms.length} firms</Typography>
      {totals.map(total => (
        <Chip key={total.label} size="small" variant="outlined" label={`${total.label}: ${total.value}`} />
      ))}
    </Stack>
  );

  const emailList = users.map(user => user.givenName + ',' + user.email).join('\n');
  const [copied, setCopied] = useState(false);

  return (
    <>
      <MUIDataTable title={title} data={firms || []} columns={columns} options={options} />

      {usersPerFirm[-1] && (
        <>
          <Divider sx={{ mt: 5, mb: 2 }} textAlign="left">
            Users without firm
          </Divider>
          <Table size="small">
            <TableHead>
              <StyledTableRow>
                <StyledTableCell align="center">Xero User Id</StyledTableCell>
                <StyledTableCell align="center">Name</StyledTableCell>
                <StyledTableCell align="center">Email</StyledTableCell>
                <StyledTableCell align="center">Creation Date</StyledTableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {usersPerFirm[-1].map((user, index) => (
                <StyledTableRow key={`user{index}`}>
                  <StyledTableCell align="left">{user.xeroUserUuid}</StyledTableCell>
                  <StyledTableCell align="left">
                    {user.familyName} {user.givenName}
                  </StyledTableCell>
                  <StyledTableCell align="left">
                    {user.email} <i>(id: {user.id})</i>
                  </StyledTableCell>
                  <StyledTableCell align="center">Created {user.creationDate}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <Box sx={{ mt: 4, maxWidth: 600 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>All users with emails ({users.length})</Typography>
          <Button
            size="small"
            startIcon={<CopyIcon fontSize="small" />}
            onClick={async () => {
              await navigator.clipboard?.writeText(emailList);
              setCopied(true);
            }}
          >
            {copied ? 'Copied' : 'Copy CSV'}
          </Button>
        </Stack>
        <TextField
          value={emailList}
          multiline
          minRows={4}
          maxRows={10}
          fullWidth
          InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
        />
      </Box>
    </>
  );
}
